import CoreUtils from '../utils/CoreUtils';

import DataItem from './DataItem';
import DataObject from './DataObject';

export default class DataObjectList extends DataItem {
  constructor(metadataRules) {
    super(metadataRules);

    this.dataObjects = [];
    this.originalDataObjects = [];

    this.dataObjectStateListener = this.dataObjectStateUpdated.bind(this);
    this.length = 0;
  }

  getElementClassType() {
    return DataObject;
  }

  resolveElementClassType() {
    var classType = this.getElementClassType();
    return CoreUtils.isDefinedAndNotNull(classType) || DataObject.prototype.isPrototypeOf(classType)
      ? classType
      : DataObject;
  }

  initDataObjects(dtoObjects, metadataRules) {
    this.dispose();

    if (CoreUtils.isUndefinedOrNull(dtoObjects) || !Array.isArray(dtoObjects))
      return;

    if (CoreUtils.isDefinedAndNotNull(metadataRules))
      this.metadataRules = metadataRules;

    var classType = this.resolveElementClassType();

    dtoObjects.forEach(
      dtoObject => {
        if (CoreUtils.isUndefinedOrNull(dtoObject))
          return;

        var dataObject = new classType(this.metadataRules);
        dataObject.initDataObject(dtoObject);
        dataObject.updateProjectedValues(this.projectedValues);
        dataObject.addStateListener(this.dataObjectStateListener);
        this.dataObjects.push(dataObject);
      }
    );

    this.originalDataObjects = Array.from(this.dataObjects);
    this.length = this.dataObjects.length;

    var state = {
      dataObjects: Array.from(this.dataObjects)
    };

    this.processItemIsModified(state);
    this.processItemHasErrors(state);
    this.notifyStateUpdated(state);
  }

  dispose() {
    this.dataObjects.forEach(dataObject => {
      dataObject.removeStateListener(this.dataObjectStateListener);
      dataObject.removeProjectedValues(this.projectedValues);
      dataObject.dispose();
    });

    this.dataObjects = [];
    this.originalDataObjects = [];

    super.dispose();
  }

  insertNewDataObject(dataObjectPosition) {
    var insertIndex = this.dataObjects.length;
    if (CoreUtils.isDefinedAndNotNull(dataObjectPosition)) {
      var insertBeforeIndex = this.dataObjects.indexOf(dataObjectPosition);
      if (insertBeforeIndex !== -1)
        insertIndex = insertBeforeIndex;
    }

    var classType = this.resolveElementClassType();

    var dataObject = new classType(this.metadataRules);
    dataObject.createDataObject();
    dataObject.updateProjectedValues(this.projectedValues);
    dataObject.addStateListener(this.dataObjectStateListener);
    this.dataObjects.splice(insertIndex, 0, dataObject);

    this.length = this.dataObjects.length;

    var state = {
      dataObjects: Array.from(this.dataObjects)
    };

    this.processItemIsModified(state);
    this.processItemHasErrors(state);
    this.notifyStateUpdated(state);

    return dataObject;
  }

  removeDataObject(dataObject, makePermanent) {
    if (CoreUtils.isUndefinedOrNull(dataObject))
      return;

    var dataObjectIndex = this.dataObjects.indexOf(dataObject);
    if (dataObjectIndex === -1)
      return;

    this.dataObjects.splice(dataObjectIndex, 1);
    dataObject.removeStateListener(this.dataObjectStateListener);
    dataObject.removeProjectedValues(this.projectedValues);

    if (makePermanent === true) {
      dataObjectIndex = this.originalDataObjects.indexOf(dataObject);
      if (dataObjectIndex !== -1)
        this.originalDataObjects.splice(dataObjectIndex, 1);
    }

    this.length = this.dataObjects.length;

    var state = {
      dataObjects: Array.from(this.dataObjects)
    };

    this.processItemIsModified(state);
    this.processItemHasErrors(state);
    this.notifyStateUpdated(state);
  }

  ensureDataObjectPermanent(dataObject) {
    var dataObjectIndex = this.dataObjects.indexOf(dataObject);
    if (dataObjectIndex === -1)
      return;

    if (this.originalDataObjects.indexOf(dataObject) === -1)
      this.originalDataObjects.splice(dataObjectIndex, 0, dataObject);

    var state = {};

    this.processItemIsModified(state);
    this.processItemHasErrors(state);
    this.notifyStateUpdated(state);
  }

  getDtoObjects() {
    return this.dataObjects
      .filter(dataObject => !dataObject.isDeleted)
      .map(dataObject => dataObject.getDtoObject());
  }

  updateProjectedValues(projectedValues) {
    super.updateProjectedValues(projectedValues);
  }

  removeProjectedValues(projectedValues) {
    this.dataObjects.forEach(dataObject =>
      dataObject.removeProjectedValues(projectedValues)
    );

    super.removeProjectedValues(projectedValues);
  }

  propogateProjectedValues() {
    super.propogateProjectedValues();

    this.dataObjects.forEach(dataObject =>
      dataObject.updateProjectedValues(this.projectedValues)
    );
  }

  revertItemValues() {
    if (!this.isModified)
      return;

    var state = {};

    if (this.isInternalListModified()) {
      this.dataObjects.forEach(dataObject => {
        if (this.originalDataObjects.indexOf(dataObject) === -1) {
          dataObject.removeStateListener(this.dataObjectStateListener);
          dataObject.removeProjectedValues(this.projectedValues);
          dataObject.dispose();
        }
      });

      this.originalDataObjects.forEach(dataObject => {
        if (this.dataObjects.indexOf(dataObject) === -1) {
          dataObject.updateProjectedValues(this.projectedValues);
          dataObject.addStateListener(this.dataObjectStateListener);
        }
      });

      this.dataObjects = Array.from(this.originalDataObjects);
      this.length = this.dataObjects.length;

      state.dataObjects = Array.from(this.dataObjects);
    }

    this.dataObjects.forEach(dataObject => {
      dataObject.revertItemValues();
    });

    this.processItemIsModified(state);
    this.processItemHasErrors(state);
    this.notifyStateUpdated(state);
  }

  commitUpdatedValues(dtoObjects) {
    if (!this.isModified || this.hasErrors)
      return;

    var state = {};

    this.originalDataObjects.forEach(dataObject => {
      if (this.dataObjects.indexOf(dataObject) === -1)
        dataObject.dispose();
    });

    if (CoreUtils.isDefinedAndNotNull(dtoObjects)) {
      if (Array.isArray(dtoObjects)) {
        this.dataObjects.forEach(dataObject => {
          dataObject.removeStateListener(this.dataObjectStateListener);
          dataObject.removeProjectedValues(this.projectedValues);
          dataObject.dispose();
        });

        this.dataObjects = [];

        var classType = this.resolveElementClassType();

        dtoObjects.forEach(
          dtoObject => {
            if (CoreUtils.isUndefinedOrNull(dtoObject))
              return;

            var dataObject = new classType(this.metadataRules);
            dataObject.initDataObject(dtoObject);
            dataObject.updateProjectedValues(this.projectedValues);
            dataObject.addStateListener(this.dataObjectStateListener);
            this.dataObjects.push(dataObject);
          }
        );
      }
    } else {
      var dataObjects = Array.from(this.dataObjects);

      dataObjects.forEach(dataObject => {
        if (dataObject.isDeleted) {
          var dataObjectIndex = this.dataObjects.indexOf(dataObject);
          if (dataObjectIndex !== -1) {
            this.dataObjects.splice(dataObjectIndex, 1);
            dataObject.removeStateListener(this.dataObjectStateListener);
            dataObject.removeProjectedValues(this.projectedValues);
          }
        } else
          dataObject.commitUpdatedValues();
      });
    }

    state.dataObjects = Array.from(this.dataObjects);

    this.originalDataObjects = Array.from(this.dataObjects);
    this.length = this.dataObjects.length;

    this.processItemIsModified(state);
    this.processItemHasErrors(state);
    this.notifyStateUpdated(state);
  }

  revertGroupItemValues() {
    this.dataObjects.forEach(dataObject => {
      dataObject.revertGroupItemValues();
    });

    super.revertGroupItemValues();
  }

  commitGroupItemValues() {
    this.dataObjects.forEach(dataObject => {
      dataObject.commitGroupItemValues();
    });

    super.commitGroupItemValues();
  }

  getState() {
    var state = super.getState();
    state.dataObjects = Array.from(this.dataObjects)
    return state;
  }

  dataObjectStateUpdated() {
    var state = {};

    var updateIsModified = this.processItemIsModified(state);
    var updateHasErrors = this.processItemHasErrors(state);

    if (!updateIsModified && !updateHasErrors)
      return;

    this.notifyStateUpdated(state);
  }

  isInternalListModified() {
    if (this.dataObjects.length !== this.originalDataObjects.length)
      return true;

    for (var index = 0; index < this.dataObjects.length; index++) {
      var dataObject = this.dataObjects[index];
      if (this.originalDataObjects.indexOf(dataObject) === -1)
        return true;
    }

    return false;
  }

  resolveItemIsModified() {
    return this.isInternalListModified()
      || this.dataObjects.some(dataObject => dataObject.isModified);
  }

  resolveItemHasErrors() {
    return this.dataObjects.some(dataObject => dataObject.hasErrors);
  }

  resolveGroupIsModified() {
    return super.resolveGroupIsModified()
      || this.dataObjects.some(dataObject => dataObject.groupIsModified);
  }

  resolveGroupHasErrors() {
    return super.resolveGroupHasErrors()
      || this.dataObjects.some(dataObject => dataObject.groupHasErrors);
  }

  entries() {
    return this.dataObjects.entries();
  }

  every(callback, arg) {
    return this.dataObjects.every(callback, arg);
  }

  find(callback) {
    return this.dataObjects.find(callback);
  }

  forEach(callback, arg) {
    this.dataObjects.forEach(callback, arg);
  }

  getAt(index) {
    return this.dataObjects[index];
  }

  indexOf(searchElement, fromIndex) {
    return this.dataObjects.indexOf(searchElement, fromIndex);
  }

  map(callback, arg) {
    return this.dataObjects.map(callback, arg);
  }

  some(callback, arg) {
    return this.dataObjects.some(callback, arg);
  }
}